/**
 * Computes SHA-256 hash of the input string (lowercased).
 * Returns empty string if crypto API unavailable or error occurs.
 * @param {string} input
 * @returns {Promise<string>}
 */
export async function computeSHA256(input) {
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
        return ''
    }
    try {
        const encoder = new TextEncoder()
        const data = encoder.encode(input.toLowerCase())
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
    } catch {
        return ''
    }
}

/**
 * Returns the GA4 client_id by stripping the GA1.X. prefix from the _ga cookie.
 * Returns null if the cookie is absent (e.g. cookies blocked).
 * @returns {string|null}
 */
export function getGA4ClientId() {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(/_ga=GA\d+\.\d+\.(.+?)(?:;|$)/)
    return match ? match[1] : null
}

/**
 * Returns the GA4 session_id from the _ga_MEASUREMENTID cookie.
 * Handles both cookie formats:
 *   Old: GS1.1.1717000000.3.1... → session_id is the plain number at index 2
 *   New: GS2.1.s1774620891$o81$... → session_id is the number after leading "s" at index 2
 * @param {string} measurementId - GA4 Measurement ID without the "G-" prefix (e.g. "4P4KZTEKKQ")
 * @returns {string|null}
 */
export function getGA4SessionId(measurementId) {
    if (typeof document === 'undefined' || !measurementId) return null
    const match = document.cookie.match(new RegExp('_ga_' + measurementId + '=([^;]+)'))
    if (!match) return null
    const segment = match[1].split('.')[2] || null
    if (!segment) return null
    // New format: segment starts with "s" followed by the session timestamp (e.g. "s1774620891$o81$...")
    const newFormat = segment.match(/^s(\d+)/)
    if (newFormat) return newFormat[1]
    // Old format: segment is already a plain number
    return segment
}

/**
 * Pushes an analytics event to window.dataLayer with common user info and event-specific name.
 * @param {Object} params
 * @param {string} params.eventName - The event name to push
 * @param {string} params.userId - User ID
 * @param {string} params.email - User email
 * @param {boolean} params.registered - Is user registered
 * @param {string} params.phone - User phone number
 * @param {Object} [params.extraData] - Additional event-specific data to merge
 */
export async function pushAnalyticsEvent({
    eventName,
    userId,
    email,
    registered,
    phone,
    extraData = {}
}) {
    if (typeof window === 'undefined' || !window.dataLayer) return

    const sha256 = await computeSHA256(email || '')

    window.dataLayer.push({
        event: eventName,
        userId,
        user_id: sha256,
        sha256,
        registered,
        phone,
        event_id: `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...extraData
    })
}
